import { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { AlertTriangle, Bot, Check, Search, X } from 'lucide-react'
import { useConfirmDialog } from '../../ui/ConfirmDialog.js'

export interface PickerAgentItem {
  id: string
  slug: string
  name: string
  type: string
  description?: string | undefined
  isEnabled: boolean
}

interface PromptAgentPickerDrawerProps {
  promptName: string
  agents: PickerAgentItem[]
  assignedAgentIds: string[]
  /**
   * agentId → name of a DIFFERENT prompt template the agent is currently bound
   * to. An agent holds exactly one prompt (`ai_agents.prompt_id`), so assigning
   * it here replaces that prompt AND overwrites the agent's system prompt text.
   * Used to warn before the overwrite happens.
   */
  otherPromptByAgentId?: Record<string, string>
  isOpen: boolean
  onClose: () => void
  onSave: (selectedIds: string[]) => void | Promise<void>
}

const TYPE_META: Record<string, { label: string; badge: string }> = {
  routing: { label: 'Routing', badge: 'bg-indigo-100 text-indigo-800' },
  response: { label: 'Response', badge: 'bg-blue-100 text-blue-800' },
  data: { label: 'Data', badge: 'bg-emerald-100 text-emerald-800' },
  custom: { label: 'Custom', badge: 'bg-purple-100 text-purple-800' },
}

function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape: () => void,
) {
  useEffect(() => {
    if (!isOpen) return
    const drawer = ref.current
    if (!drawer) return

    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'input, textarea, button, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    const first = focusables()[0]
    first?.focus()

    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onEscape()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const list = focusables()
      if (list.length === 0) return
      const firstEl = list[0]!
      const lastEl = list[list.length - 1]!
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [isOpen, onEscape, ref])
}

function groupByType<T extends { type: string }>(items: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const list = groups.get(item.type) ?? []
    list.push(item)
    groups.set(item.type, list)
  }
  return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
}

export default function PromptAgentPickerDrawer({
  promptName,
  agents,
  assignedAgentIds,
  otherPromptByAgentId = {},
  isOpen,
  onClose,
  onSave,
}: PromptAgentPickerDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const { confirm, Dialog, isOpen: isConfirmOpen } = useConfirmDialog()

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(assignedAgentIds))
      setQuery('')
    }
  }, [isOpen, assignedAgentIds])

  // Suspended while the override confirmation is up, so Escape/Tab act on the
  // dialog rather than dismissing the whole drawer behind it.
  useFocusTrap(drawerRef, isOpen && !isConfirmOpen, onClose)

  // Agents being newly linked here that already hold a different prompt —
  // saving replaces that prompt and overwrites their system prompt text.
  const overriddenAgents = useMemo(
    () =>
      agents
        .filter((a) => selected.has(a.id) && !!otherPromptByAgentId[a.id])
        .map((a) => ({ name: a.name, previousPrompt: otherPromptByAgentId[a.id] as string })),
    [agents, selected, otherPromptByAgentId],
  )

  if (!isOpen) return null

  const q = query.trim().toLowerCase()
  const filtered = q
    ? agents.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.slug.toLowerCase().includes(q) ||
          a.type.toLowerCase().includes(q),
      )
    : agents

  const grouped = groupByType(filtered)

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleType(type: string): void {
    const typeIds = agents.filter((a) => a.type === type).map((a) => a.id)
    const allSelected = typeIds.every((id) => selected.has(id))
    setSelected((prev) => {
      const next = new Set(prev)
      for (const id of typeIds) {
        if (allSelected) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }

  function selectAll(): void {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const a of filtered) next.add(a.id)
      return next
    })
  }

  function clearAll(): void {
    setSelected(new Set())
  }

  async function persist(): Promise<void> {
    setIsSaving(true)
    try {
      await onSave(Array.from(selected))
    } finally {
      setIsSaving(false)
    }
  }

  function handleSave(): void {
    if (overriddenAgents.length === 0) {
      void persist()
      return
    }

    const lines = overriddenAgents
      .map((a) => `• ${a.name} — currently uses "${a.previousPrompt}"`)
      .join('\n')
    const count = overriddenAgents.length
    confirm({
      title: count === 1 ? 'Override the existing prompt?' : `Override ${count} existing prompts?`,
      message:
        `An agent can only use one prompt template at a time. ` +
        `Assigning "${promptName}" will replace the prompt currently linked to ` +
        `${count === 1 ? 'this agent' : 'these agents'} and overwrite their system prompt text:\n\n${lines}\n\n` +
        `Are you sure you want to assign it like this?`,
      confirmLabel: 'Yes, override',
      cancelLabel: 'Cancel',
      onConfirm: () => {
        void persist()
      },
    })
  }

  const selectedCount = selected.size
  const totalCount = agents.length

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="prompt-agent-picker-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" />

      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[480px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <Bot className="h-4 w-4 shrink-0 text-gray-500" />
            <div className="min-w-0">
              <span id="prompt-agent-picker-title" className="block min-w-0 truncate text-sm font-semibold text-gray-900">
                Assign to Agents
              </span>
              <span className="block truncate text-[10px] text-gray-500">{promptName}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search and bulk actions */}
        <div className="space-y-3 border-b border-gray-100 p-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents..."
              className="w-full rounded-sm border border-gray-300 bg-white py-2 pl-9 pr-3 text-xs text-gray-700 outline-none focus:border-[#3454cf] focus:ring-1 focus:ring-[#3454cf]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-500">
              {selectedCount} of {totalCount} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="text-[11px] font-medium text-[#3454cf] hover:underline"
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-[11px] font-medium text-gray-600 hover:text-gray-900"
              >
                Clear all
              </button>
            </div>
          </div>
        </div>

        {/* Agent list */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-24">
          {grouped.length === 0 ? (
            <div className="py-8 text-center">
              <Bot className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              <p className="text-sm font-medium text-gray-600">No agents found</p>
              <p className="text-[11px] text-gray-400">Try a different search term.</p>
            </div>
          ) : (
            grouped.map(([type, items]) => {
              const typeIds = items.map((a) => a.id)
              const allSelected = typeIds.every((id) => selected.has(id))
              const someSelected = typeIds.some((id) => selected.has(id)) && !allSelected
              const meta = TYPE_META[type] ?? { label: type, badge: 'bg-gray-100 text-gray-800' }

              return (
                <div key={type} className="rounded-sm border border-gray-200 bg-white p-3">
                  <label className="mb-2 flex cursor-pointer items-center gap-2 border-b border-gray-100 pb-2 text-xs font-semibold text-gray-900">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected
                      }}
                      onChange={() => toggleType(type)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                    />
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.badge}`}>{meta.label}</span>
                    <span className="ml-auto text-[10px] font-normal text-gray-500">{items.length}</span>
                  </label>

                  <div className="space-y-2">
                    {items.map((a) => (
                      <label
                        key={a.id}
                        className="flex cursor-pointer items-start gap-2 rounded-sm p-1.5 transition-colors hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(a.id)}
                          onChange={() => toggle(a.id)}
                          className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-900">
                            {a.name}
                            {selected.has(a.id) && <Check className="h-3 w-3 text-emerald-600" />}
                            {!a.isEnabled && (
                              <span className="rounded bg-gray-100 px-1 py-0.5 text-[9px] text-gray-500">Disabled</span>
                            )}
                          </div>
                          {a.description && (
                            <p className="text-[10px] leading-snug text-gray-500">{a.description}</p>
                          )}
                          {otherPromptByAgentId[a.id] && (
                            <p
                              className={`mt-0.5 flex items-center gap-1 text-[10px] leading-snug ${
                                selected.has(a.id) ? 'font-medium text-amber-700' : 'text-gray-400'
                              }`}
                            >
                              <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
                              <span className="min-w-0 truncate">
                                {selected.has(a.id) ? 'Will replace' : 'Currently uses'} “{otherPromptByAgentId[a.id]}”
                              </span>
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Sticky footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4">
          {overriddenAgents.length > 0 && (
            <p className="mb-3 flex items-start gap-1.5 rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-2 text-[10px] leading-snug text-amber-800">
              <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
              <span>
                {overriddenAgents.length === 1
                  ? '1 selected agent already uses another prompt template. Saving will override it.'
                  : `${overriddenAgents.length} selected agents already use another prompt template. Saving will override them.`}
              </span>
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] text-gray-500">{selectedCount} selected</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-sm border border-gray-200 px-4 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#3454cf]"
              >
                {isSaving && <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog />
    </div>
  )
}
